import html2canvas from "html2canvas";
import { create } from "zustand";
import * as SharedChatResponse from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";

export enum PreviewMode {
  Desktop = "desktop",
  MobilePortrait = "mobilePortrait",
  MobileLandscape = "mobileLandscape",
  Tablet = "tablet",
}

interface ProjectPreviewActions {
  initialize: () => void;
  setIframeRef: (iframeRef: React.RefObject<HTMLIFrameElement>) => void;
  setPreviewMode: (previewMode: PreviewMode) => void;
  increaseScale: () => void;
  decreaseScale: () => void;
  resetScale: () => void;
  generateScreenshot: () => Promise<HTMLCanvasElement | undefined>;
  reload: () => void;
  dispose: () => void;
}

interface ProjectPreviewState {
  url: string | null;
  version: number;
  mode: PreviewMode;
  scale: number;

  actions: ProjectPreviewActions;
}

const useProjectPreviewStore = create<ProjectPreviewState>()((set, get) => {
  let unsubscribeProjectHistory: (() => void) | null = null;
  let unsubscribeLatestMsg: (() => void) | null = null;
  let iframeRef: React.RefObject<HTMLIFrameElement> | null = null;

  function handlePreviewUrl(
    latestEvent: SharedChatResponse.MessageBody | undefined
  ) {
    const projectId = useProjectStore.getState().projectId;
    if (
      latestEvent &&
      (latestEvent as SharedChatResponse.MessageBody).content === ""
    ) {
      set({
        url: null,
        version: 0,
      });
      return;
    }

    if (!latestEvent || !projectId) {
      return;
    }
    const { protocol, host } = window.location;
    const finalUrl = `${protocol}//${host}/preview/${projectId}`;
    if (finalUrl !== get().url) {
      console.log("Preview URL: ", finalUrl);
    }
    set((state) => ({
      url: finalUrl,
      version: state.version + 1,
    }));
  }

  return {
    url: null,
    version: 0,
    mode: PreviewMode.MobilePortrait,
    scale: 1,

    actions: {
      initialize: () => {
        unsubscribeProjectHistory = useProjectStore.subscribe(
          (state) => state.history,
          (history) => {
            if (history && history.length > 0 && get().url === null) {
              const latestPreviewUrl = [...history]
                .reverse()
                .find(
                  (msg) =>
                    msg.type ===
                    SharedMessageTypes.MessageTypeEnum.Enum.previewUrl
                );
              handlePreviewUrl(latestPreviewUrl);
            }
          }
        );
        unsubscribeLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (latestMsg) => {
            if (
              latestMsg?.type ===
              SharedMessageTypes.MessageTypeEnum.Enum.previewUrl
            ) {
              handlePreviewUrl(latestMsg);
            }
          }
        );
      },

      setIframeRef: (ref: React.RefObject<HTMLIFrameElement>) => {
        iframeRef = ref;
      },

      setPreviewMode: (previewMode: PreviewMode) => set({ mode: previewMode }),

      increaseScale: () =>
        set((state) => ({
          scale: Math.max(0.5, Math.min(2, state.scale + 0.1)),
        })),

      decreaseScale: () =>
        set((state) => ({
          scale: Math.max(0.5, Math.min(2, state.scale - 0.1)),
        })),

      resetScale: () => set({ scale: 1 }),

      generateScreenshot: async () => {
        if (!iframeRef) return;

        console.log("iframeRef::", iframeRef);

        const iframeBody = iframeRef!.current?.contentDocument?.body;
        if (!iframeBody) return;

        console.log("iframeBody::", iframeBody);

        return await html2canvas(iframeBody, {
          background: "#ffffff",
        });
      },

      reload: () => {
        set((state) => ({
          version: state.version + 1,
        }));
      },

      dispose: () => {
        if (unsubscribeProjectHistory) {
          unsubscribeProjectHistory();
          unsubscribeProjectHistory = null;
        }
        if (unsubscribeLatestMsg) {
          unsubscribeLatestMsg();
          unsubscribeLatestMsg = null;
        }
        set({
          url: null,
          version: 0,
          mode: PreviewMode.MobilePortrait,
          scale: 1,
        });
      },
    },
  };
});

export const useProjectPreviewUrl = () =>
  useProjectPreviewStore((state) => state.url);
export const useProjectPreviewVersion = () =>
  useProjectPreviewStore((state) => state.version);
export const useProjectPreviewMode = () =>
  useProjectPreviewStore((state) => state.mode);
export const useProjectPreviewScale = () =>
  useProjectPreviewStore((state) => state.scale);
export const useProjectPreviewActions = () =>
  useProjectPreviewStore((state) => state.actions);
